import type { CSSProperties } from '@material-ui/styles'
import type { Theme } from '@material-ui/core'

export interface BlankLineStyles {
  /** Merge into the `& .ProseMirror p` rule. */
  paragraph: CSSProperties
  /** Assign to the `& .ProseMirror p::after` rule. */
  after: CSSProperties
}

/**
 * Makes a blank line in a boxed RTE exactly one line tall — no more, no less — so the template
 * editor matches what the email actually renders (#22160).
 *
 * An empty ProseMirror paragraph has no intrinsic height, and two separate hacks have been
 * fighting over who gives it one:
 *
 * - `getPresentationContentStyles` adds `p:after { content: ''; display: inline-block; width: 0 }`.
 *   On a genuinely empty `<p>` that contributes nothing, but when the paragraph ends in a `<br>`
 *   — either a real hard break (what pasting from Word/Gmail produces) or ProseMirror's own
 *   trailing-break hack — the pseudo-element lands on the line *after* the break and the
 *   paragraph renders two lines tall.
 * - ProseMirror's `<br class="ProseMirror-trailingBreak">`, whose visibility has now flipped
 *   twice (#21916/#21917 showed it, #22034/#22120 hid it again), each time silently changing
 *   how tall a blank line is.
 *
 * Measured against the real component, every one of these renders as a single line in the
 * Postmark email layouts (`.user-content p { min-height: 26px }`) but not in the editor:
 *
 * | body            | email | editor before | editor after |
 * |-----------------|-------|---------------|--------------|
 * | `<p></p>`       | 1     | 1 (2 in 7.11) | 1            |
 * | `<p><br></p>`   | 1     | 2             | 1            |
 * | `<p>a<br></p>`  | 1     | 2             | 1            |
 *
 * So: stop the `:after` hack generating that second line box, and give paragraphs one line of
 * height outright — the same approach the email layouts already take.
 *
 * Inline (Presentation v2) editors are left alone. They deliberately keep the trailing break on
 * empty paragraphs so a blank line stays clickable for the FloatingMenu (#21916), and their
 * editing surface has to keep matching the rendered presentation, which uses
 * `getPresentationContentStyles` directly.
 */
export const blankLineStyles = (theme: Theme, inline: boolean): BlankLineStyles => {
  if (inline) return { paragraph: {}, after: {} }

  // `em` (not px) so the blank line tracks the paragraph's own font size through
  // responsiveFontSizes; body1's unitless line-height is exactly one line box.
  return {
    paragraph: { minHeight: `${theme.typography.body1.lineHeight}em` },
    after: { content: 'none' }
  }
}
